/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type { ApiErrorDto, PlatformsDto, UpdatePlatformsDto } from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Where the workspace publishes reels.
 */
export const getPlatforms = (id: string, signal?: AbortSignal) => {
  return customInstance<PlatformsDto>({
    url: `/api/v1/workspaces/${id}/platforms`,
    method: 'GET',
    signal,
  });
};

export const getGetPlatformsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/platforms`] as const;
};

export const getGetPlatformsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPlatforms>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlatformsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatforms>>> = ({ signal }) =>
    getPlatforms(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPlatformsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPlatforms>>
>;
export type GetPlatformsQueryError = ErrorType<ApiErrorDto>;

export function useGetPlatforms<
  TData = Awaited<ReturnType<typeof getPlatforms>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlatforms>>,
          TError,
          Awaited<ReturnType<typeof getPlatforms>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlatforms<
  TData = Awaited<ReturnType<typeof getPlatforms>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlatforms>>,
          TError,
          Awaited<ReturnType<typeof getPlatforms>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlatforms<
  TData = Awaited<ReturnType<typeof getPlatforms>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Where the workspace publishes reels.
 */

export function useGetPlatforms<
  TData = Awaited<ReturnType<typeof getPlatforms>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlatforms>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPlatformsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Set where the workspace publishes reels.
 */
export const updatePlatforms = (
  id: string,
  updatePlatformsDto: BodyType<UpdatePlatformsDto>,
  signal?: AbortSignal,
) => {
  return customInstance<PlatformsDto>({
    url: `/api/v1/workspaces/${id}/platforms`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updatePlatformsDto,
    signal,
  });
};

export const getUpdatePlatformsMutationKey = () => ['updatePlatforms'] as const;

export const getUpdatePlatformsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePlatforms>>,
    TError,
    UpdatePlatformsMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePlatforms>>,
  TError,
  UpdatePlatformsMutationVariables,
  TContext
> => {
  const mutationKey = getUpdatePlatformsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePlatforms>>,
    UpdatePlatformsMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return updatePlatforms(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePlatformsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePlatforms>>
>;
export type UpdatePlatformsMutationBody = BodyType<UpdatePlatformsDto>;
export type UpdatePlatformsMutationError = ErrorType<ApiErrorDto>;
export type UpdatePlatformsMutationVariables = {
  id: string;
  data: BodyType<UpdatePlatformsDto>;
};

/**
 * @summary Set where the workspace publishes reels.
 */
export const useUpdatePlatforms = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updatePlatforms>>,
      TError,
      UpdatePlatformsMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updatePlatforms>>,
  TError,
  UpdatePlatformsMutationVariables,
  TContext
> => {
  return useMutation(getUpdatePlatformsMutationOptions(options), queryClient);
};
